import { TableCell, TableRow } from "@mui/material";
import { IncidentDocument, IncidentDocumentRowViewModel } from "./IncidentDocument.types.ts";
import { isNullOrWhitespace } from "../../../common/stringOperations.ts";
import { EntityType } from "../../../common/Common.types.ts";
import { DotLegalEmptyState, DotLegalOverflowMenu, ITableHeader } from "@dotlegal/dotlegal-ui-components";
import React, { useState } from "react";
import { useTranslation } from "../../../localization/useTranslation.ts";
import { useUserContext } from "../../../auth/userContextProvider/UserContextProvider.tsx";
import IncidentDocumentDeleteDialog from "./incidentDocumentDeleteDialog/IncidentDocumentDeleteDialog.tsx";
import { useDotLegalFileUploadDataMapping } from "../../../common/components/dotLegalFileUploadTable/DotLegalFileUploadTable.hooks.ts";
import NoSharings from "../../../processingActivity/processingActivityOverview/processingActivitySharingsTab/noSharings.svg?react";
import { useDocumentPreviewManager } from "../../../common/components/documentPreview/useDocumentPreviewManager.ts";
import DocumentPreviewLink from "../../../common/components/documentPreview/DocumentPreviewLink.tsx";
import { useStateUrlParams } from "../../../common/hooks/useStateUrlParams.ts";
import { usePlanContext } from "../../../auth/planProvider/PlanProvider.tsx";
import AuditLogDialog from "../../../auditlogDialog/AuditLogDialog.tsx";
import { useBestPracticeDialogsHook } from "../../../common/components/bestPracticeCheckDialog/BestPracticeCheckDialog.hooks.ts";
import BestPracticeCoverageResult from "../../../common/components/bestPracticeCheckResultDialog/BestPracticeCoverageResult.tsx";
import BestPracticeCheckDialog from "../../../common/components/bestPracticeCheckDialog/BestPracticeCheckDialog.tsx";
import BestPracticeCheckResultDialog from "../../../common/components/bestPracticeCheckResultDialog/BestPracticeCheckResultDialog.tsx";
import TableHeaderLock from "../../../common/components/TableHeaderLock";
import { getAiQualityScanOverflowMenuItem } from "@common/components/bestPracticeCheckDialog/aiQualityScanOverflowMenuItem.ts";
import { useAiFeatureAccess } from "../../../plan/aiFeatureGate/useAiFeatureAccess";
import DotLegalTableWithFilterBar from "@common/components/dotLegalTableWithFilterBar/DotLegalTableWithFilterBar";
import { useIncidentDocumentFilters } from "./IncidentDocument.hooks.ts";

export interface IncidentDocumentTableProps {
    incidentId: string;
    incidentName?: string;
    isLoading?: false | true;
    documents: Array<IncidentDocumentRowViewModel> | undefined;
    refetchDocuments?: () => void;
    onEditDocument?: (document: IncidentDocumentRowViewModel) => void;
    isPdf?: boolean;
}

function IncidentDocumentTable(props: IncidentDocumentTableProps) {
    const hasDocuments = props.isLoading || (props.documents ?? []).length > 0;

    const [selectedDocument, setSelectedDocument] = useState<IncidentDocument>();
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [showAuditLogDialog, setShowAuditLogDialog] = useStateUrlParams("document-auditlog", null);

    const { downloadDocumentVersion } = useDotLegalFileUploadDataMapping();

    const { translateString, translateDate } = useTranslation();
    const { permissions } = useUserContext();
    const { platformFeatures } = usePlanContext();
    const { showAiQualityAssurance, planActivated } = useAiFeatureAccess();
    const {
        bestPracticeCheckDocument,
        setBestPracticeCheckDocument,
        bestPracticeCheckDocumentToQualityAssurance,
        setBestPracticeCheckDocumentToQualityAssurance,
        bestPracticeCheckInitialResult,
        setBestPracticeCheckInitialResult,
        onCheckComplete,
    } = useBestPracticeDialogsHook();
    const { filteredDocuments, getFilterOptions, onFilterChange, hasActiveFilters, pinnedFilterIds, onPinnedFilterIdsChange } = useIncidentDocumentFilters(
        props.incidentId,
        props.documents,
        props.isPdf
    );

    const { previewableDocuments, handleDocumentClick, getPreviewHref } = useDocumentPreviewManager({
        documents: filteredDocuments ?? [],
        entityType: EntityType.IncidentLog,
        entityId: props.incidentId,
        entityTitle: props.incidentName,
    });

    function getHeaders() {
        const headers = Array<ITableHeader<IncidentDocumentRowViewModel>>();
        headers.push({ property: "name", text: translateString("name"), align: "left", showOnMobile: true });
        headers.push({ property: "fileName", text: translateString("document"), align: "left", showOnMobile: true });
        headers.push({ property: "created", text: translateString("created"), align: "left", showOnMobile: true });
        if (showAiQualityAssurance) {
            headers.push({
                property: "aiQualityAssuranceResult",
                text: translateString("aiQualityScan"),
                extraHeaderContent: !planActivated ? <TableHeaderLock tooltip={translateString("aiAddOnPurchaseTooltip")} /> : undefined,
            });
        }
        return headers;
    }

    const refetchDocuments = () => {
        if (props.refetchDocuments) {
            props.refetchDocuments();
        }
    };

    return (
        <>
            {hasDocuments ? (
                <DotLegalTableWithFilterBar
                    filterBar={
                        props.isPdf
                            ? undefined
                            : {
                                  isLoading: props.isLoading ?? false,
                                  filterData: getFilterOptions(),
                                  fetchFilterData: true,
                                  onMenuClose: onFilterChange,
                                  onRemove: onFilterChange,
                                  pinnedFilterIds: pinnedFilterIds,
                                  onPinnedFilterIdsChange: onPinnedFilterIdsChange,
                              }
                    }
                    headers={getHeaders()}
                    rowsPerPage={30}
                    defaultOrderBy={"name"}
                    defaultOrder={"asc"}
                    isLoading={props.isLoading}
                    data={filteredDocuments ?? []}
                    isPdf={props.isPdf}
                    clickableRows
                    hasOverflowMenu
                    emptyText={translateString(hasActiveFilters ? "noDataMatchingCriteria" : "noDocumentsMatchesQuery")}
                    renderRow={(row, i) => (
                        <TableRow key={i} onClick={() => handleDocumentClick(row)}>
                            <TableCell>
                                <DocumentPreviewLink previewHref={getPreviewHref(row)} onPreviewClick={() => handleDocumentClick(row)}>
                                    {row.name}
                                </DocumentPreviewLink>
                            </TableCell>
                            <TableCell>{row.fileName ?? row.link}</TableCell>
                            <TableCell>{translateDate(row.created)}</TableCell>
                            {showAiQualityAssurance && (
                                <TableCell>
                                    {!isNullOrWhitespace(row.fileName) && (
                                        <BestPracticeCoverageResult
                                            result={row.aiQualityAssuranceResult}
                                            isTooLargeForAi={row.isTooLargeForAi}
                                            onClick={() => {
                                                if (row.aiQualityAssuranceResult) {
                                                    setBestPracticeCheckDocument({
                                                        documentId: row.documentId,
                                                    });
                                                } else {
                                                    setBestPracticeCheckDocumentToQualityAssurance({
                                                        documentId: row.documentId,
                                                    });
                                                }
                                            }}
                                        />
                                    )}
                                </TableCell>
                            )}
                            <TableCell align={"right"}>
                                <DotLegalOverflowMenu items={getMenuItems(row)} />
                            </TableCell>
                        </TableRow>
                    )}
                />
            ) : (
                <DotLegalEmptyState icon={<NoSharings />} text={translateString("noDocumentsAttachedToLegalEntity")} />
            )}

            {showDeleteDialog && (
                <IncidentDocumentDeleteDialog
                    onCloseDialog={() => setShowDeleteDialog(false)}
                    onDeletedDocument={() => refetchDocuments()}
                    incidentLogId={props.incidentId}
                    selectedDocument={selectedDocument!}
                />
            )}

            {!isNullOrWhitespace(showAuditLogDialog) && (
                <AuditLogDialog
                    entityType={EntityType.IncidentLogDocument}
                    entityId={showAuditLogDialog!}
                    entityName={(props.documents ?? []).find((x) => x.incidentLogDocumentId === showAuditLogDialog)?.name ?? ""}
                    onDialogClose={() => setShowAuditLogDialog(null)}
                />
            )}

            {bestPracticeCheckDocumentToQualityAssurance && (
                <BestPracticeCheckDialog
                    documentId={bestPracticeCheckDocumentToQualityAssurance.documentId}
                    onDialogClose={() => {
                        setBestPracticeCheckDocumentToQualityAssurance(undefined);
                        refetchDocuments();
                    }}
                    onCheckComplete={onCheckComplete}
                />
            )}

            {bestPracticeCheckDocument && (
                <BestPracticeCheckResultDialog
                    documentId={bestPracticeCheckDocument.documentId}
                    initialResult={bestPracticeCheckInitialResult}
                    onDialogClose={() => {
                        setBestPracticeCheckDocument(undefined);
                        setBestPracticeCheckInitialResult(undefined);
                        refetchDocuments();
                    }}
                />
            )}
        </>
    );

    function getMenuItems(document: IncidentDocumentRowViewModel) {
        let menuItems = [];

        if (permissions.incidentLogPermissions.edit) {
            menuItems.push({
                menuItem: translateString("edit"),
                onClick: () => props.onEditDocument?.(document),
            });
        }

        if (isNullOrWhitespace(document.fileName)) {
            menuItems.push({
                menuItem: translateString("openLink"),
                onClick: () => window.open(document.link!, "_blank"),
            });
        } else {
            menuItems.push({
                menuItem: translateString("download"),
                onClick: () => downloadDocumentVersion(document.documentId, document.dataId!, document.fileName!, EntityType.IncidentLog, props.incidentId),
            });
        }

        if (permissions.companyDocumentationPermissions.delete) {
            menuItems.push({
                menuItem: translateString("delete"),
                onClick: () => {
                    setSelectedDocument({
                        documentId: document.documentId,
                        name: document.name,
                        fileName: "",
                        link: document.link ?? "",
                    });
                    setShowDeleteDialog(true);
                },
            });
        }

        if (permissions.canAccessAuditLog) {
            menuItems.push({
                menuItem: translateString("auditlog"),
                onClick: () => {
                    setShowAuditLogDialog(document.incidentLogDocumentId);
                },
                planMenuItemInfo: {
                    hasAccess: platformFeatures.auditLog,
                    menuName: translateString("auditlog"),
                    tooltip: translateString("upgradePrivacyPlanToday"),
                },
            });
        }

        if (showAiQualityAssurance && !isNullOrWhitespace(document.fileName)) {
            menuItems.push(
                getAiQualityScanOverflowMenuItem(
                    () => {
                        if (document.aiQualityAssuranceResult) {
                            setBestPracticeCheckDocument({ documentId: document.documentId });
                        } else {
                            setBestPracticeCheckDocumentToQualityAssurance({ documentId: document.documentId });
                        }
                    },
                    planActivated,
                    document.isTooLargeForAi,
                    translateString
                )
            );
        }

        return menuItems;
    }
}

export default IncidentDocumentTable;
